import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import AdminNavbar from './AdminNavbar';
import './AdminModern.css';

const AllSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [attendFilter, setAttendFilter] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 20
  });
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();

  const fetchSubmissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.limit
      });

      if (search) params.append('search', search);
      if (attendFilter) params.append('attend', attendFilter);

      const apiBase = process.env.REACT_APP_API_URL || '';
      const submissionsUrl = apiBase.endsWith('/') ? apiBase + 'admin/submissions' : apiBase + '/admin/submissions';
      const response = await axios.get(`${submissionsUrl}?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setSubmissions(response.data.submissions);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error('Error fetching submissions:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
      } else {
        setError('Failed to load submissions');
      }
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.limit, search, attendFilter, navigate]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleFilterChange = (e) => {
    setAttendFilter(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, currentPage: value }));
  };

  const clearFilters = () => {
    setSearch('');
    setAttendFilter('');
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const handleBack = () => {
    navigate('/admin/dashboard');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record? This action cannot be undone.')) {
      return;
    }

    setDeletingId(id);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const apiBase = process.env.REACT_APP_API_URL || '';
      const deleteUrl = apiBase.endsWith('/') ? apiBase + `admin/event-attendance/${id}` : apiBase + `/admin/event-attendance/${id}`;
      await axios.delete(deleteUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Refresh the data after successful deletion
      fetchSubmissions();
    } catch (err) {
      console.error('Error deleting record:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
      } else {
        setError('Failed to delete record');
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      console.log('Starting CSV download...');
      const apiBase = process.env.REACT_APP_API_URL || '';
      const downloadUrl = apiBase.endsWith('/') ? apiBase + 'admin/download-attending-csv' : apiBase + '/admin/download-attending-csv';
      const response = await axios.get(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });

      console.log('Response received:', response);
      console.log('Response data type:', typeof response.data);
      console.log('Response data size:', response.data.size);

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'all_submissions.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('Download completed successfully');
    } catch (err) {
      console.error('Error downloading CSV:', err);
      console.error('Error response:', err.response);
      console.error('Error message:', err.message);
      
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
      } else {
        setError(`Failed to download CSV file: ${err.message}`);
      }
    }
  };

  if (loading) {
    return (
      <div className="admin-page-wrapper">
        <div className="admin-bg" />
        <AdminNavbar />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page-wrapper">
        <div className="admin-bg" />
        <AdminNavbar />
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </div>
    );
  }

  return (
    <div className="admin-page-wrapper">
      <div className="admin-bg" />
      <AdminNavbar />
      <div className="admin-main-container">
        {/* Header */}
        <div className="admin-glass-card">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={handleBack} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h4" component="h1">
                All Submissions
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadCSV}
              sx={{
                backgroundColor: '#000000',
                color: '#ffffff',
                fontWeight: '500',
                '&:hover': {
                  backgroundColor: '#333333',
                  color: '#ffffff'
                }
              }}
            >
              Download All Submissions CSV
            </Button>
          </Box>
        </div>

        {/* Search and Filter Section */}
        <div className="admin-glass-card">
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }} className="search-filter-container">
            <TextField
              label="Search"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search by reference number, participant name, or supplier name"
              sx={{ minWidth: { xs: '100%', sm: 300 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            <FormControl sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel>Attendance</InputLabel>
              <Select
                value={attendFilter}
                label="Attendance"
                onChange={handleFilterChange}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Yes">Attending</MenuItem>
                <MenuItem value="No">Not Attending</MenuItem>
              </Select>
            </FormControl>

            <Button
              className="admin-btn"
              onClick={clearFilters}
              disabled={!search && !attendFilter}
              sx={{
                backgroundColor: '#000000',
                color: '#ffffff',
                fontWeight: '500',
                '&:hover': {
                  backgroundColor: '#333333',
                  color: '#ffffff'
                },
                '&:disabled': {
                  backgroundColor: '#cccccc',
                  color: '#666666'
                }
              }}
            >
              Clear Filters
            </Button>
          </Box>
        </div>

        {/* Results Summary */}
        <div className="admin-glass-card">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {submissions.length} of {pagination.totalCount} submissions
            {search && ` for "${search}"`}
            {attendFilter && ` (${attendFilter})`}
          </Typography>
        </div>

        {/* Submissions Table */}
        <div className="admin-glass-card">
          <div className="admin-table-responsive">
            <TableContainer>
              <Table className="admin-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Reference Number</TableCell>
                    <TableCell>Participant Name</TableCell>
                    <TableCell>Supplier Name</TableCell>
                    <TableCell>Attendance</TableCell>
                    <TableCell>Submitted At</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submissions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No submissions found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    submissions.map((submission, index) => (
                      <TableRow key={index}>
                        <TableCell>{submission.ref_number}</TableCell>
                        <TableCell>{submission.participant_name}</TableCell>
                        <TableCell>{submission.supplier_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={submission.attend}
                            color={submission.attend === 'Yes' ? 'success' : 'error'}
                            size="small"
                            sx={{ fontWeight: 600, letterSpacing: 1 }}
                          />
                        </TableCell>
                        <TableCell>{formatDate(submission.submitted_at)}</TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => handleDelete(submission.id)}
                            disabled={deletingId === submission.id}
                            color="error"
                            size="small"
                            title="Delete record"
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(244, 67, 54, 0.1)'
                              }
                            }}
                          >
                            {deletingId === submission.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DeleteIcon />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="admin-glass-card">
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllSubmissions; 